/**
 * CandidateLeanStepper — per-candidate support capture at the door.
 *
 * Field side of candidate-attribution Phase 2. When a walk has a multi-candidate
 * slate, the canvasser records one support lean *per candidate* instead of a
 * single race-wide summary. This renders a stacked list — one candidate per row,
 * each with a 6-way lean selector plus an explicit "Skip — didn't ask" default.
 *
 * Two rules this is built around
 * ------------------------------
 *
 * 1. **Skip writes nothing.** A skipped candidate is genuinely absent from the
 *    payload — it does NOT collapse to `undecided`. "I didn't ask about Garcia"
 *    and "Garcia is a toss-up here" are different facts, and defaulting the
 *    former to the latter would fabricate data the canvasser never collected.
 *    So `null` (skip) is the initial value for every candidate and is never
 *    emitted.
 *
 * 2. **It never fetches.** The slate arrives on the `ShiftBriefing` payload and
 *    is passed straight through (see `SlateChip` and `canvassOfflineQueue`).
 *    This is props-in / callback-out, no effects, no client — a canvasser on a
 *    rural porch with no LTE must still get a usable form.
 *
 * Controlled: the parent (`PerDoorCard`) owns the `value` map so it can build
 * `response.candidate_leans` on submit. `value` holds ONLY rated candidates;
 * an absent key means skipped.
 */
import React from 'react';

import type { CandidateLean, LeanSource } from '@/api/leans';
import type { CandidateBrief } from '@/types/candidate';
import { candidateShortName } from '@/types/candidate';

// The six leans, in the order a canvasser reads them — most-supportive to
// most-opposed, then refused. Wire spelling (snake_case) is the enum value; the
// label is what the volunteer taps.
const LEAN_OPTIONS: ReadonlyArray<{
  value: CandidateLean;
  label: string;
  /** Tailwind bg for the SELECTED state — a warm→cool ramp, refused neutral. */
  color: string;
}> = [
  { value: 'strong_support', label: 'Strong support', color: 'bg-emerald-600' },
  { value: 'lean_support', label: 'Lean support', color: 'bg-emerald-500' },
  { value: 'undecided', label: 'Undecided', color: 'bg-slate-500' },
  { value: 'lean_oppose', label: 'Lean oppose', color: 'bg-orange-500' },
  { value: 'strong_oppose', label: 'Strong oppose', color: 'bg-red-600' },
  { value: 'refused', label: 'Refused', color: 'bg-zinc-600' },
];

/** How a lean captured through this stepper was sourced: a presented slate. */
export const STEPPER_LEAN_SOURCE: LeanSource = 'slate';

export interface CandidateLeanStepperProps {
  /**
   * The slate for this walk, straight off `ShiftBriefing.candidates`. An empty
   * array renders nothing (the caller can gate on this too).
   */
  candidates: CandidateBrief[];
  /**
   * Rated candidates only, keyed by candidate id. An absent key == skipped and
   * will produce no `candidate_leans` entry on submit.
   */
  value: Record<number, CandidateLean>;
  /** `lean === null` clears the rating back to "skipped". */
  onChange: (candidateId: number, lean: CandidateLean | null) => void;
  disabled?: boolean;
}

export function CandidateLeanStepper({
  candidates,
  value,
  onChange,
  disabled = false,
}: CandidateLeanStepperProps): React.ReactElement | null {
  if (candidates.length === 0) return null;

  return (
    <div data-testid="candidate-lean-stepper">
      <div className="text-xs text-fg-muted uppercase tracking-wide mb-1">
        Candidate support (tap one per candidate)
      </div>
      <div className="text-[11px] text-fg-muted mb-2">
        Leave a candidate on <strong>Skip</strong> if you didn't ask about them —
        skipped candidates aren't recorded.
      </div>

      <ul className="space-y-3">
        {candidates.map((c) => {
          const selected = value[c.id];
          return (
            <li
              key={c.id}
              className="rounded-lg border border-border-base bg-bg-base p-3"
              data-testid={`candidate-lean-row-${c.id}`}
            >
              <div className="mb-2 min-w-0">
                <div className="text-sm font-semibold text-fg-primary truncate">
                  {candidateShortName(c)}
                </div>
                <div className="text-xs text-fg-muted truncate">
                  {c.office}
                  {c.party ? ` · ${c.party}` : ''}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {/* Skip is the default and always available — tapping it clears
                    any rating back to "didn't ask". */}
                <button
                  type="button"
                  disabled={disabled}
                  aria-pressed={selected === undefined}
                  onClick={() => onChange(c.id, null)}
                  className={`text-xs font-semibold py-2 px-2 rounded border transition-colors disabled:opacity-40 ${
                    selected === undefined
                      ? 'bg-bg-elevated border-accent text-fg-primary ring-1 ring-accent'
                      : 'bg-bg-raised border-border-base text-fg-muted hover:text-fg-secondary'
                  }`}
                >
                  Skip — didn't ask
                </button>

                {LEAN_OPTIONS.map((opt) => {
                  const on = selected === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={disabled}
                      aria-pressed={on}
                      onClick={() => onChange(c.id, opt.value)}
                      className={`text-xs font-semibold py-2 px-2 rounded border transition-colors text-white disabled:opacity-40 ${
                        on
                          ? `${opt.color} border-white/70 ring-2 ring-white`
                          : `${opt.color} border-transparent opacity-55 hover:opacity-100`
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default CandidateLeanStepper;
